import { atom } from 'recoil';
import * as K from 'common/constants';
import { LogEntry } from 'common/graphqlTypes';
import { addCommas } from 'common/util/number';
import { recoilSelector } from 'common/util/recoil-selector';
import { recoilSelectorFamily } from 'common/util/recoil-selector-family';
import { ErrorResult } from 'hooks/useLRQ/useLRQ';
import { generateLoglineNode } from 'hooks/useLRQ/useLRQ.queries';
import { selectedTokenState } from 'components/common/Managers/RouteStateManager.state';
import client from 'api/apolloClient';

import { createAtomWithSubscription } from '../service/atomFabric';
import { networkErrorObject } from './apolloClient';

/**
 * Watches changes to event data in the Apollo client cache. This atom has no semantic value, but other selectors can
 * use this atom to "subscribe" to event data in the Apollo cache.
 */
export const eventCacheWatcher = atom<{}>({
  key: K.RECOIL_ATOM_KEY.EVENT_CACHE_WATCHER,
  default: {},
});

/**
 * A record of query ids to {@link ErrorResult}. This record might be populated from a top-level error in the GraphQL
 * response, or from the error property on an individual {@link QueryRequestResult} in a successful response.
 * TODO: caution; this is likely going to change soon {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4398}
 */
export const lrqErrorsById = atom<Record<string, ErrorResult>>({
  key: K.RECOIL_ATOM_KEY.LRQ_ERRORS_BY_ID,
  default: K.EMPTY_OBJECT,
});

export const networkErrorState = createAtomWithSubscription(networkErrorObject, K.RECOIL_ATOM_KEY.NETWORK_ERROR);

/**
 * The {@link LogEntry} data in the Apollo cache associated with the given continuationToken.
 */
export const logEntryState = recoilSelectorFamily<LogEntry, string>({
  key: 'logEntryState',
  get:
    (continuationToken) =>
    ({ get: getRecoilValue }): LogEntry => {
      getRecoilValue(eventCacheWatcher);
      const { logEntry } =
        client.readFragment({
          id: 'LogEntryEdge:' + JSON.stringify({ continuationToken }),
          fragment: generateLoglineNode(),
        }) || {};
      return logEntry;
    },
});

/**
 * The {@link LogEntry} data in the Apollo cache associated with the current user selection.
 */
export const selectedLogEntryState = recoilSelector<LogEntry>({
  key: 'selectedLogEntryState',
  get: ({ get: getRecoilValue }) => {
    getRecoilValue(eventCacheWatcher);
    const continuationToken = getRecoilValue(selectedTokenState);
    return getRecoilValue(logEntryState(continuationToken));
  },
});

/**
 * The most accurate available matchCount for the currently active search on the search logs page, which comes from the
 * PLOT request of the timeline line graph. If this request is not fulfilled by a timeseries, the count will be exact.
 * Otherwise, the count is derived from a timeseries with 30 second granularity, and it is not quite exact for the very
 * start & end of the query time range.
 */
export const activeSearchMatchCountEstimateState = atom<number>({
  key: K.RECOIL_ATOM_KEY.ACTIVE_SEARCH_MATCH_COUNT_ESTIMATE,
  default: 0,
});

/**
 * The most accurate available integer matchCount for the currently active search on the search logs page.
 */
export const activeSearchMatchCountState = recoilSelector<number>({
  key: K.RECOIL_SELECTOR_KEY.ACTIVE_SEARCH_MATCH_COUNT,
  get: ({ get }) => {
    const matchCount = get(activeSearchMatchCountEstimateState);
    return Math.round(matchCount);
  },
});

/**
 * The title displayed on the {@see TimelineLegend} component for the search logs page, which is derived from the best
 * available estimate of the match count for the currently active search.
 */
export const logSearchMatchCountTitleState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.LOG_SEARCH_MATCH_COUNT_TITLE,
  get: ({ get }) => {
    const matchCount = get(activeSearchMatchCountEstimateState);
    const isCountInteger = Number.isInteger(matchCount);
    const prefix = isCountInteger ? '' : '~';
    const formattedMatchCount = addCommas(matchCount, 10000, 0);
    const pluralSuffix = matchCount === 1 ? '' : 's';
    return `${prefix}${formattedMatchCount} matching event${pluralSuffix}`;
  },
});
